/**
 * How long a session has been running, and how to say it.
 *
 * The same rule as `alter-web/infra/lambda/sessions/timer.py`, and
 * deliberately the same shape. Two implementations that drift are worse than
 * one that is slow, so the two test files assert the same boundaries; the
 * browser needs its own only because it repaints every second and cannot ask
 * the server for each tick.
 *
 * `now` is an argument here for the same reason it is there: every
 * interesting case is a boundary, and a function that fetches its own time
 * cannot be tested at one.
 *
 * Times are epoch **seconds**, matching the store. `Date.now() / 1000` at the
 * call site, never milliseconds -- a millisecond value here reads as a
 * session that started fifty thousand years from now.
 */

/** Four hours, then the clock stops. See the Python module for why. */
export const CAP_SECONDS = 4 * 60 * 60;

const isNumber = (value) => typeof value === 'number' && Number.isFinite(value);

function pausedSeconds(pauses, until) {
  let total = 0;
  for (const pause of pauses ?? []) {
    const at = pause?.at;
    if (!isNumber(at) || at >= until) continue;
    // An open pause runs to `until`: the clock is stopped right now.
    const stop = Math.min(isNumber(pause?.until) ? pause.until : until, until);
    if (stop > at) total += stop - at;
  }
  return total;
}

/**
 * Seconds actually spent: pauses removed, and never past the cap.
 *
 * A session this cannot read reads as zero rather than NaN. A NaN reaching
 * `formatClock` puts "NaN:NaN" on screen, which is a worse answer than a
 * clock that has not started.
 */
export function elapsedSeconds(session, now) {
  const startedAt = session?.startedAt;
  if (!isNumber(startedAt) || !isNumber(now)) return 0;

  const cap = startedAt + CAP_SECONDS;
  const endedAt = session?.endedAt;
  const finish = Math.min(isNumber(endedAt) ? endedAt : now, cap);

  const span = finish - startedAt;
  if (span <= 0) return 0;
  return Math.max(0, span - pausedSeconds(session?.pauses, finish));
}

/** Floored, never rounded: a second that has not finished has not happened. */
export function formatClock(seconds) {
  const whole = isNumber(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
  const hours = Math.floor(whole / 3600);
  const minutes = Math.floor((whole % 3600) / 60);
  const secs = whole % 60;
  const pad = (n) => String(n).padStart(2, '0');
  // The hours column appears only once there are hours, so a ten-minute break
  // reads 10:05 rather than 0:10:05 -- the same choice the desktop timer made.
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(secs)}` : `${minutes}:${pad(secs)}`;
}
